/**
 * Barrel export for generated permission definitions
 *
 * Add exports here as new permission modules are generated.
 * Run: pnpm run gen:permissions -- <module-name>
 */

import type { LucideIcon } from 'lucide-react';
import {
  LucideBell,
  LucideCoins,
  LucideDollarSign,
  LucideFactory,
  LucideFileOutput,
  LucideFolderOpen,
  LucideHardHat,
  LucideMessageSquare,
  LucidePackage,
  LucideSearch,
  LucideSettings,
  LucideTruck,
  LucideUsers,
} from 'lucide-react';

import { collabPermissions } from './collab.permissions';
import type { CollabPermissionKey } from './collab.permissions';
import { corePermissions } from './core.permissions';
import type { CorePermissionKey } from './core.permissions';
import { crmPermissions } from './crm.permissions';
import type { CrmPermissionKey } from './crm.permissions';
import { filesPermissions } from './files.permissions';
import type { FilesPermissionKey } from './files.permissions';
import { financePermissions } from './finance.permissions';
import type { FinancePermissionKey } from './finance.permissions';
import { foremanPermissions } from './foreman.permissions';
import type { ForemanPermissionKey } from './foreman.permissions';
import { inventoryPermissions } from './inventory.permissions';
import type { InventoryPermissionKey } from './inventory.permissions';
import { notificationPermissions } from './notification.permissions';
import type { NotificationPermissionKey } from './notification.permissions';
import { pricingPermissions } from './pricing.permissions';
import type { PricingPermissionKey } from './pricing.permissions';
import { productionPermissions } from './production.permissions';
import type { ProductionPermissionKey } from './production.permissions';
import { searchPermissions } from './search.permissions';
import type { SearchPermissionKey } from './search.permissions';
import { tradePermissions } from './trade.permissions';
import type { TradePermissionKey } from './trade.permissions';
import { transportationPermissions } from './transportation.permissions';
import type { TransportationPermissionKey } from './transportation.permissions';
import type { ModulePermissions } from './types';

/**
 * Aggregate of all permission modules for use in PermissionsEditor
 * Update this array as new modules are generated
 */

// Type definitions
export * from './types';

// Generated permission modules
export {
  collabPermissions,
  type CollabPermissionKey,
} from './collab.permissions';
export { corePermissions, type CorePermissionKey } from './core.permissions';
export { crmPermissions, type CrmPermissionKey } from './crm.permissions';
export { filesPermissions, type FilesPermissionKey } from './files.permissions';
export {
  financePermissions,
  type FinancePermissionKey,
} from './finance.permissions';
export {
  foremanPermissions,
  type ForemanPermissionKey,
} from './foreman.permissions';
export {
  inventoryPermissions,
  type InventoryPermissionKey,
} from './inventory.permissions';
export {
  notificationPermissions,
  type NotificationPermissionKey,
} from './notification.permissions';
export {
  pricingPermissions,
  type PricingPermissionKey,
} from './pricing.permissions';
export {
  productionPermissions,
  type ProductionPermissionKey,
} from './production.permissions';
export {
  searchPermissions,
  type SearchPermissionKey,
} from './search.permissions';
export { tradePermissions, type TradePermissionKey } from './trade.permissions';
export {
  transportationPermissions,
  type TransportationPermissionKey,
} from './transportation.permissions';

export type AllPermissionKeys =
  | CollabPermissionKey
  | CorePermissionKey
  | CrmPermissionKey
  | FilesPermissionKey
  | FinancePermissionKey
  | ForemanPermissionKey
  | InventoryPermissionKey
  | NotificationPermissionKey
  | PricingPermissionKey
  | ProductionPermissionKey
  | SearchPermissionKey
  | TradePermissionKey
  | TransportationPermissionKey;

/**
 * Icon mapping for permission modules
 * Maps module name to its display icon
 */
export const moduleIcons: Record<string, LucideIcon> = {
  collab: LucideMessageSquare,
  core: LucideSettings,
  crm: LucideUsers,
  files: LucideFolderOpen,
  finance: LucideCoins,
  foreman: LucideHardHat,
  inventory: LucidePackage,
  notification: LucideBell,
  pricing: LucideDollarSign,
  production: LucideFactory,
  search: LucideSearch,
  trade: LucideFileOutput,
  transportation: LucideTruck,
};

export const allPermissionModules: ModulePermissions[] = [
  collabPermissions,
  corePermissions,
  crmPermissions,
  filesPermissions,
  financePermissions,
  foremanPermissions,
  inventoryPermissions,
  notificationPermissions,
  pricingPermissions,
  productionPermissions,
  searchPermissions,
  tradePermissions,
  transportationPermissions,
];

const EXTERNAL_ENTITY_NAMES = new Set(['salesOrder', 'booking', 'load']);

export const externalPermissionModules: ModulePermissions[] =
  allPermissionModules
    .map((mod) => ({
      ...mod,
      entities: mod.entities.filter((e) => EXTERNAL_ENTITY_NAMES.has(e.name)),
    }))
    .filter((mod) => mod.entities.length > 0);
